/* eslint react/jsx-pascal-case:0 no-mixed-operators:0 */

import { useEffect, useState, useContext } from 'react';
import { 
  Box, 
  Stack, 
  Button,
  ButtonGroup,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'

import { 
  MaterialReactTable, 
  useMaterialReactTable, 
} from 'material-react-table';

import { useLocalStorage } from 'hooks/use-local-storage'

import { Layout as DashboardLayout } from 'layouts/portal/layout';
import { Tracking } from 'components/Tracking';
import { Departments, availableCategoryGroups, AuthContext, BudgetEdit } from 'components';

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});


export const Budgets = () => {
  const [ visibleItems, setVisibleItems ] = useState([]);
  const [ reportData, setReportData ] = useState({});
  const [ columns, setColumns ] = useState([]);
  const [ columnOrder, setColumnOrder ] = useLocalStorage('budget-column-order', []);
  const [ selectedDepartment, setSelectedDepartment ] = useLocalStorage('budget-selected-department', availableCategoryGroups[0]);
  const [ columnVisibility, setColumnVisibility ] = useLocalStorage('budget-column-visibility', [])
  const { items, user, collections, selectedSeason, selectedStore, customCategories, exchangeRates, selectedCollection, accountBudgets, account, storeCurrency } = useContext(AuthContext);

  // console.log('collections', collections)
  // console.log('reportData', reportData)
  // console.log('customCategories', customCategories)
  // console.log('Category budgets', accountBudgets)
  // console.log('Collection/Season budgets', account.acctStores[selectedStore].collectionBudgets )
  // console.log('selectedDepartment', selectedDepartment)
  // console.log('selectedCategoryGroups', selectedCategoryGroups)
  // console.log('collections', collections)
  /*
  Columns:
    Dynamic - Iterate all season collections
    Static - Collection Category totals, Season Totals
  Rows:
    Dynamic - Iterate all department categories
    Static - Collection Total
  */

  useEffect(() => { Tracking.track('Portal - Budget') }, [])

  useEffect(() => {
    //                  | collection A | collection B | ...
    // category 
    //    budget        | Comes from accountCategorySectionBudgets
    //    spent         | Calculate on commited items
    //    balance
    if (!items || !items[selectedSeason]) { setReportData({}); return; }

    const spendings = {}
    Object.keys(items[selectedSeason]).filter(item_id => items[selectedSeason]?.[item_id]?.['categoryType'] === availableCategoryGroups.indexOf(selectedDepartment)).forEach((item_id) => {
    // Object.keys(items[selectedSeason]).forEach((item_id) => {
      let collection = items[selectedSeason]?.[item_id].collectionId || '-'
      let category = items[selectedSeason]?.[item_id].categoryId || '-'

      if (!spendings.hasOwnProperty(category)) {
        spendings[category] = {
          displayName: customCategories[category] ? customCategories[category].category : '-',
          department: customCategories[category] ? customCategories[category].category_group : '-',
          spend: 0, // Total spend of all items
          collections: {}
        }
      }

      if (!spendings[category]?.['collections'].hasOwnProperty(collection)) {
        spendings[category]['collections'][collection] = {
          displayName: collections[collection] ? collections[collection].displayName : '-',
          spend: 0, // Dynamically calculated
          budget: 0 // Set below from account budgets
        }
      }
      
      if (items[selectedSeason]?.[item_id].priceInfo?.price && items[selectedSeason]?.[item_id].orderStatus === 1) {
        let quantity = items[selectedSeason]?.[item_id].priceInfo?.quantity || 0
        let currency = items[selectedSeason]?.[item_id].priceInfo?.currency
        let price = parseFloat(items[selectedSeason]?.[item_id].priceInfo?.price)

        if (currency !== storeCurrency && exchangeRates.hasOwnProperty(currency)) {
          // Change rate to USD
          price /= exchangeRates[currency].rate
          // Change rate to Store Currency
          price *= exchangeRates[storeCurrency].rate
        }
        spendings[category].spend += price * quantity
        spendings[category]['collections'][collection].spend += price * quantity
      }
    })
    setReportData(spendings)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedSeason, selectedCollection, selectedDepartment, customCategories])

  // Update columns of the table
  useEffect(() => {
    let default_columns = [
      { 
        id: 'category',
        accessorKey: 'category',
        accessorFn: (e) => {
          // return `${e.category} ${e.category_id}`
          return `${e.category}`
        },
        header: 'Category',
        enableHiding: false,
        size: 50,
      },
      { 
        id: 'budget_titles',
        accessorKey: 'budget_titles',
        header: '',
        enableHiding: false,
        size: 50,
        Cell: ({ row, cell }) => 
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{padding: 0}}>Budget</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{padding: 0}}>Spent</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{padding: 0}}>Balance</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ,
      }
    ]

    let dynamic_columns = []
    Object.keys(collections).forEach((collection) => {
      if (collection !== 'all' && collection !== '-') {
        dynamic_columns.push({
          id: collection,
          accessorKey: collections[collection].displayName,
          header: collections[collection].displayName,
          displayOrder: collections[collection].displayOrder,
          Cell: ({ row, cell }) => {
            let budget = 0
            let spend = 0

            if (row.original.category_id === 'total') {
              budget = account?.acctStores?.[selectedStore]?.collectionBudgets?.[cell.column.id]?.budget || 0
              spend = Object.keys(reportData).reduce((prev, curr) => prev + (reportData[curr]?.collections?.[cell.column.id]?.spend || 0), 0)
            } else {
              budget = accountBudgets[cell.column.id]?.[row.original.category_group_id]?.[row.original.category_id]?.budget || 0
              spend = reportData[row.original.category_id]?.collections[cell.column.id]?.spend || 0
            }

            let balance = budget - spend;
          
            return <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: budget < 0 ? '#ff3b53' : '#111927'}}>
                    {row.original.category_id === 'total' ? 
                    (<BudgetEdit season={cell.column.id} category={row.original} budget={formatter.format(budget)} />) :
                    (<BudgetEdit collection={collection} category={row.original} budget={formatter.format(budget)} />)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: spend < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(spend)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: balance < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(balance)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          },
        })
      }
    })

    let collection_columns = []
    if (Object.keys(collections).length > 2) {
      collection_columns.push({
        id: '---collection_category_totals',
        header: 'Collection Total',
        Cell: ({ row, cell }) => {
          let budget = 0;
          let spend = 0;
  
          if (row.original.category_id === 'total') {
            spend = Object.keys(reportData).reduce((prev, curr) => prev + reportData[curr].spend - (reportData[curr].collections['-']?.spend || 0), 0)
          }
  
          Object.keys(collections).forEach((collection) => {
            if (collection !== 'all' && collection !== '-') {
              if (row.original.category_id === 'total') {
                budget += account.acctStores[selectedStore]?.collectionBudgets?.[collection]?.budget || 0
              } else {
                budget += accountBudgets[collection]?.[row.original.category_group_id]?.[row.original.category_id]?.budget || 0
                spend += reportData[row.original.category_id]?.collections[collection]?.spend || 0
              }
            }
          })
  
          let balance = budget - spend;
  
          return <Table>
            <TableBody>
              <TableRow>
              <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: budget < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(budget)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: spend < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(spend)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: balance < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(balance)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        },
      })

      collection_columns.push({
        id: '---no_collection',
        header: 'Not in a Collection',
        Cell: ({ row, cell }) => {
          let spend = -1;
          if (row.original.category_id === 'total') {
            spend = Object.keys(reportData).reduce((prev, curr) => prev + (reportData[curr]?.collections['-']?.spend || 0), 0)
          } else {
            spend = reportData[row.original.category_id]?.collections['-']?.spend || 0
          }
          return <Table>
            <TableBody>
              <TableRow>
              <TableCell sx={{padding: 0, width: 100, textAlign: 'right'}}></TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: spend < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(spend)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{padding: 0, width: 100, textAlign: 'right'}}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        },
      })
    }

    const new_columns = [
      ...default_columns, 
      ...dynamic_columns.sort((a, b) => a.displayOrder > b.displayOrder ? 1 : -1),
      ...collection_columns,
      ...[
        {
          id: '---season_totals',
          header: 'Season Total',
          Cell: ({ row, cell }) => {
            let budget = 0;
            let spend = -1;

            if (row.original.category_id !== 'total') {
              budget = accountBudgets[selectedSeason]?.[row.original.category_group_id]?.[row.original.category_id]?.budget || 0
              spend = reportData[row.original.category_id]?.spend || 0
            } else {
              budget = account.acctStores[selectedStore].collectionBudgets?.[selectedSeason]?.budget || 0
              spend = Object.keys(reportData).reduce((prev, curr) => prev + reportData[curr].spend, 0)
            }

            let balance = budget - spend;

            return <Table>
              <TableBody>
                <TableRow>
                <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: budget < 0 ? '#ff3b53' : '#111927'}}>
                  {row.original.category_id === 'total' ? (
                    <BudgetEdit season={selectedSeason} category={row.original} budget={formatter.format(budget)} />
                  ) : (
                    <BudgetEdit category={row.original} budget={formatter.format(budget)} />
                  )}
                </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: spend < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(spend)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{padding: 0, width: 100, textAlign: 'right', color: balance < 0 ? '#ff3b53' : '#111927'}}>{formatter.format(balance)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          },
        }
      ]
    ]

    setColumns(new_columns)
    
    // Sort the columns
    const new_column_order = new_columns.map((column) => column.id).sort((a, b) => {
      if (a.id < b.id) {
        return -1;
      } else if (a.id > b.id) {
        return 1;
      } else {
        return 0;
      }
    })  
    setColumnOrder(new_column_order);
  }, [items, accountBudgets, account, collections, selectedDepartment, reportData])

  // Update rows of the table
  useEffect(() => {
    let report_rows = []
    Object.keys(customCategories).forEach((category) => {
      const sorting = customCategories && customCategories[category]?.sorting || []
      if (category !== 'sections')
      report_rows.push({
        ...customCategories[category],
        priority: sorting.indexOf(category)
      })
    })

    const new_visible_items = report_rows
      .filter((row) => row.category_group === selectedDepartment)
      .sort((a, b) => a.priority > b.priority ? 1 : -1)
      new_visible_items.push({
        category_id: 'total',
        category: 'Total'
      })
    setVisibleItems(new_visible_items)
  }, [items, accountBudgets, selectedDepartment, selectedSeason, selectedCollection, customCategories, reportData])


  const table = useMaterialReactTable({
    columns: columns,
    data: visibleItems,
    density: 'comfortable',
    enableColumnActions: false,
    enableColumnOrdering: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableSorting: false,
    enableStickyHeader: true,
    defaultColumn: {
      minSize: 100, //allow columns to get smaller than default
      maxSize: 400, //allow columns to get larger than default
      size: 180, //make columns wider by default
    },
    layoutMode: 'grid-no-grow',
    enableColumnResizing: false,
    state: {
      // columnOrder,
      // columnPinning: { left: ['category', 'budget_titles'], right: ['collection_category_totals', 'no_collection', 'season_totals'] }
      columnVisibility,
      columnOrder,
      columnPinning: { left: ['category', 'budget_titles'] }
      // columnPinning: { left: columns.map((column) => column.id) }
    },
    onColumnVisibilityChange: setColumnVisibility,
    muiTableHeadCellProps: {
      align: 'right',
      sx: {
        width: 'calc(var(--col-category-size) * 1.2px)',
      }
    },
    muiTableBodyCellProps: {
      align: 'right',
      sx: {
        width: 'calc(var(--col-category-size) * 1.2px)',
      }
    },
  })
    
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DashboardLayout>
        <Typography variant="h2">Budgets</Typography>
        {user?.userRole == 4 ? (
          <p>To access the budget, please contact your FAVES admin.</p>
        ) : (
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              py: 4,
            }}
          >
            <Departments department={selectedDepartment} onChange={setSelectedDepartment} />
            <Stack style={{position: 'relative'}} spacing={3} sx={{marginBottom: 10, marginTop: 1}}>
              <MaterialReactTable table={table} />
            </Stack>
          </Box>
        )}
      </DashboardLayout>
    </LocalizationProvider>
  );
};
